// ── Shared design system ─────────────────────────────────────────────────────
// One spacing / radius / type system + a small set of primitives (Btn, IconBtn,
// Stat) so every surface of the portal reads as one cohesive product rather
// than a stack of ad-hoc styles. Colours reuse the app's CSS-variable tokens
// (C / var(--*)) from styles.js so light + dark themes and their contrast
// ratios are inherited automatically. First adopted by the Outdoor Guide
// redesign; being rolled out across the portal.
import { useState } from "react";
import { C } from "./styles";

// 4px spacing scale — use these, not arbitrary pixel values.
export const SP = { 1: "4px", 2: "8px", 3: "12px", 4: "16px", 5: "20px", 6: "24px", 8: "32px", 10: "40px" };
// One radius family.
export const RAD = { sm: "8px", md: "10px", lg: "14px" };
export const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
// Small uppercase section label (an "eyebrow"). text2 keeps AA contrast in
// light mode while staying visually secondary.
export const eyebrow = { fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.text2, margin: 0 };

const BTN_VARIANTS = {
  primary: { background: C.primary, color: "#fff", border: "1px solid transparent" },
  success: { background: C.success, color: "#fff", border: "1px solid transparent" },
  neutral: { background: "var(--surface-2)", color: C.text1, border: "1px solid var(--border-2)" },
  ghost: { background: "transparent", color: C.text2, border: "1px solid transparent" },
  danger: { background: "transparent", color: C.danger, border: "1px solid var(--border-2)" },
};

// Single button primitive — consistent height, radius, weight, focus ring
// (via .ui-btn CSS), loading spinner, and disabled treatment.
export function Btn({ variant = "neutral", size = "md", loading = false, children, style, className, ...rest }) {
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.neutral;
  const disabled = rest.disabled || loading;
  const pad = size === "sm" ? "6px 12px" : "10px 16px";
  const fs = size === "sm" ? "12px" : "13px";
  return (
    <button
      {...rest}
      disabled={disabled}
      className={"ui-btn ui-btn--" + variant + (className ? " " + className : "")}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: SP[2], fontFamily: "inherit", fontSize: fs, fontWeight: 600, lineHeight: 1, padding: pad, borderRadius: RAD.sm, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap", ...v, ...style }}
    >
      {loading && <span className="ui-spin" aria-hidden="true" style={{ width: "13px", height: "13px", borderRadius: "50%", border: "2px solid currentColor", borderTopColor: "transparent", flexShrink: 0 }} />}
      {children}
    </button>
  );
}

// Small square icon button (remove / discard / delete) — always labelled.
export function IconBtn({ label, danger = false, style, ...rest }) {
  return (
    <button
      aria-label={label} title={label}
      {...rest}
      className={"ui-btn ui-iconbtn" + (danger ? " ui-btn--danger" : "")}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", padding: 0, borderRadius: RAD.sm, background: "transparent", border: "1px solid var(--border-2)", color: danger ? C.danger : C.text2, cursor: rest.disabled ? "wait" : "pointer", fontFamily: "inherit", fontSize: "14px", lineHeight: 1, flexShrink: 0, ...style }}
    />
  );
}

// A labelled state indicator: colour dot + text (never colour alone → a11y).
const STAT_DOTS = { ready: C.success, sent: C.blue, incomplete: C.warning, success: C.success, warning: C.warning, danger: C.danger, info: C.blue, neutral: C.text3 };
export function Stat({ tone = "neutral", children }) {
  const dot = STAT_DOTS[tone] || C.text3;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: SP[2], fontSize: "12px", color: C.text2, fontWeight: 500 }}>
      <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: dot, flexShrink: 0 }} />
      {children}
    </span>
  );
}

// A small image thumbnail with graceful fallback. Chrome can't render HEIC
// (common from iPhones), so on any load error we fall back to emoji + filename
// rather than a broken-image icon.
export function ImgThumb({ url, previewUrl, name, size = 40, emojiOnlyFallback = false }) {
  const [failed, setFailed] = useState(false);
  // Browsers can't render HEIC inline; when a server-generated JPEG preview
  // exists, use it. Otherwise fall back to the original (fine for jpg/png).
  const src = previewUrl || url;
  if (failed || !src) {
    if (emojiOnlyFallback) {
      return <span aria-hidden="true" title={name} style={{ fontSize: "14px" }}>🖼️</span>;
    }
    return (
      <span style={{ fontSize: "12px", color: C.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
        🖼️ {name}
      </span>
    );
  }
  return (
    <img
      src={src} alt={name} title={name} loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: size, height: size, objectFit: "cover", borderRadius: RAD.sm, border: "1px solid var(--border-2)", background: "var(--surface-2)", flexShrink: 0, display: "block" }}
    />
  );
}
